import React from "react";
import { motion } from "framer-motion";
import { MapPin } from "lucide-react";
import { useContent } from "../context/ContentContext";
import { resolveImage } from "../lib/api";
import { Reveal } from "../components/Reveal";
import RichText from "../components/RichText";
import Gallery from "../components/Gallery";

const galleryFallback = [
  "https://images.unsplash.com/photo-1519741497674-611481863552?w=1000&q=80",
  "https://images.unsplash.com/photo-1465495976277-4387d4b0b4c6?w=1000&q=80",
  "https://images.unsplash.com/photo-1522673607200-164d1b6ce486?w=1000&q=80",
  "https://images.unsplash.com/photo-1511285560929-80b456fea0bc?w=1000&q=80",
  "https://images.unsplash.com/photo-1520854221256-17451cc331bf?w=1000&q=80",
  "https://images.unsplash.com/photo-1583939003579-730e3918a45a?w=1000&q=80",
];

const Details = () => {
  const { content } = useContent();
  const d = content?.details || {};

  return (
    <div data-testid="details-page">
      {/* Horizontal venue hero */}
      <section className="relative h-[70vh] min-h-[480px] overflow-hidden">
        <motion.img
          initial={{ scale: 1.12 }} animate={{ scale: 1 }} transition={{ duration: 1.6, ease: [0.22, 1, 0.36, 1] }}
          src={resolveImage(d.heroImageUrl)}
          alt={d.venueName || "The venue"}
          className="absolute inset-0 w-full h-full object-cover"
        />
        <div className="absolute inset-0 bg-gradient-to-t from-navy/60 via-navy/10 to-transparent" />
        <div className="absolute bottom-0 left-0 right-0 max-w-7xl mx-auto px-6 pb-14">
          <Reveal>
            <span className="uppercase tracking-[0.3em] text-xs text-blush font-bold">Wedding Details</span>
            <h1 className="mt-3 font-serif text-5xl md:text-7xl text-white tracking-tight">{d.sectionTitle}</h1>
          </Reveal>
        </div>
      </section>

      {/* Text + image asymmetrical */}
      <section className="py-28 md:py-36">
        <div className="max-w-7xl mx-auto px-6 grid lg:grid-cols-2 gap-14 lg:gap-20 items-center">
          <Reveal className="order-2 lg:order-1">
            <RichText html={d.bodyHtml} className="text-lg text-navy" />
            <div className="mt-10 grid sm:grid-cols-2 gap-8">
              <div>
                <h3 className="font-serif text-2xl text-navy mb-3">{d.ceremonyTitle}</h3>
                <RichText html={d.ceremonyHtml} className="text-navy" />
              </div>
              <div>
                <h3 className="font-serif text-2xl text-navy mb-3">{d.receptionTitle}</h3>
                <RichText html={d.receptionHtml} className="text-navy" />
              </div>
            </div>
          </Reveal>
          <Reveal delay={0.1} className="order-1 lg:order-2">
            <div className="relative aspect-[4/5] overflow-hidden rounded-lg shadow-[0_30px_70px_rgba(38,62,76,0.15)]">
              <img src={resolveImage(d.sideImageUrl)} alt="Celebration" className="w-full h-full object-cover" loading="lazy" />
            </div>
          </Reveal>
        </div>
      </section>

      {/* Venue anchor */}
      <section id="venue" className="scroll-mt-28 pb-28 md:pb-36">
        <div className="max-w-7xl mx-auto px-6">
          <Reveal>
            <div className="bg-navy text-offwhite rounded-2xl px-8 md:px-14 py-14 md:py-20 flex flex-col md:flex-row md:items-center justify-between gap-8">
              <div>
                <span className="uppercase tracking-[0.3em] text-xs text-blush font-bold">The Venue</span>
                <h2 className="font-serif text-4xl md:text-5xl mt-3">{d.venueName}</h2>
                <p className="mt-4 inline-flex items-center gap-2 text-offwhite/80"><MapPin size={18} className="text-blush" /> {d.venueAddress}</p>
              </div>
              <a
                href={`https://maps.google.com/?q=${encodeURIComponent((d.venueName || "") + " " + (d.venueAddress || ""))}`}
                target="_blank" rel="noreferrer"
                className="shrink-0 rounded-full border border-blush/50 text-blush px-8 py-4 uppercase tracking-[0.18em] text-sm hover:bg-blush hover:text-navy transition-colors duration-300"
                data-testid="venue-map-link"
              >
                View on map
              </a>
            </div>
          </Reveal>
        </div>
      </section>

      {/* Gallery anchor */}
      <section id="gallery" className="scroll-mt-28 pb-32">
        <div className="max-w-7xl mx-auto px-6">
          <Reveal className="mb-12 text-center">
            <span className="uppercase tracking-[0.3em] text-xs text-dusty font-bold">Moments</span>
            <h2 className="font-serif text-4xl md:text-5xl text-navy mt-3">A glimpse of the celebration</h2>
          </Reveal>
          <Gallery category="details" fallback={galleryFallback} />
        </div>
      </section>
    </div>
  );
};

export default Details;
